/**
 * Ad-platform click IDs. Meta Ads, Google Ads and TikTok Ads append only their own click ID
 * (`fbclid` / `gclid` / `ttclid`) to the destination URL — none of them adds `utm_source`. Because
 * attribution was keyed solely on `utm_source`, every paid visit was discarded before it was
 * recorded, leaving "Resultado de campanhas" empty for merchants running ads (VT-893).
 *
 * Each click ID maps to the channel that issued it plus a fallback campaign tag, so paid traffic
 * still reaches the campaign report — which only counts rows carrying a `utm_campaign` — when the
 * ad link has no campaign tag of its own.
 */

import { normalizeUtmCampaign, normalizeUtmSource, type UtmSource } from '@/lib/utmSources';

type AdClickId = {
  /** Query parameter the ad platform appends to the destination link. */
  param: string;
  /** Channel the click came from. Constrained to `UTM_SOURCES` so reports stay on known channels. */
  utmSource: UtmSource;
  /** Campaign tag applied when the ad link carries no `utm_campaign`. */
  fallbackCampaign: string;
};

/**
 * Ordered by precedence: a URL carrying both click IDs (rare, but possible when an ad link is
 * reshared) is attributed to the first match.
 */
const AD_CLICK_IDS: readonly AdClickId[] = [
  { param: 'fbclid', utmSource: 'meta', fallbackCampaign: 'meta-ads' },
  { param: 'ttclid', utmSource: 'tiktok', fallbackCampaign: 'tiktok-ads' },
  { param: 'gclid', utmSource: 'google', fallbackCampaign: 'google-ads' },
];

/** Campaign tags generated for untagged ad traffic — the panel renders these with a friendly label. */
export const AD_FALLBACK_CAMPAIGNS = AD_CLICK_IDS.map((c) => c.fallbackCampaign);

const AD_FALLBACK_CAMPAIGN_SET = new Set<string>(AD_FALLBACK_CAMPAIGNS);

/** True when a campaign tag was synthesized from an ad click ID rather than named by the merchant. */
export function isAdFallbackCampaign(campaign: string): boolean {
  return AD_FALLBACK_CAMPAIGN_SET.has(campaign);
}

export type CampaignAttribution = {
  utmSource: UtmSource | null;
  utmCampaign: string | null;
};

/** Minimal read surface shared by `URLSearchParams` and next/navigation's `ReadonlyURLSearchParams`. */
type ReadableParams = { get(name: string): string | null };

function hasValue(raw: string | null): boolean {
  return typeof raw === 'string' && raw.trim().length > 0;
}

/**
 * Resolve the channel/campaign pair for a landing URL.
 *
 * An explicit `utm_source` always wins, so merchant-tagged links keep behaving exactly as before.
 * A click ID only fills in what the ad platform's link left untagged: it supplies the channel, and
 * the fallback campaign tag only when `utm_campaign` is absent.
 */
export function resolveCampaignAttribution(params: ReadableParams): CampaignAttribution {
  const utmCampaign = normalizeUtmCampaign(params.get('utm_campaign'));

  const explicitSource = normalizeUtmSource(params.get('utm_source'));
  if (explicitSource) return { utmSource: explicitSource, utmCampaign };

  const adClick = AD_CLICK_IDS.find((c) => hasValue(params.get(c.param)));
  if (!adClick) return { utmSource: null, utmCampaign };

  return {
    utmSource: adClick.utmSource,
    utmCampaign: utmCampaign ?? adClick.fallbackCampaign,
  };
}
